import React from 'react';
import type { FosterHome } from '../../lib/fosterAdapters';
import type { SortDir } from '../../lib/types';
import type { RosterSortKey } from '../../hooks/usePagedFosterHomes';
import { formatPhoneNumber } from '../../lib/utils';
import { SkeletonRows } from '../../components/SkeletonRow/SkeletonRow';
import Button from '../../components/Button/Button';
import Pagination from '../../components/Pagination/Pagination';
import CollapsibleSection from './CollapsibleSection';
import TableHead, { type TableColumn } from './TableHead';
import { ACTION_COLUMN_WIDTH } from './columnWidths';
import styles from './Devices.module.scss';

/**
 * Widths are chosen so the Action column starts and ends exactly where the Action column of
 * "Devices with a Foster" does — the two tables stack on the Fosters tab, and a View button
 * that sat a little left of the Actions menu above it read as a misalignment rather than a
 * different table. The lead four therefore have to fill `100 - ACTION_COLUMN_WIDTH`.
 */
export const ROSTER_COLUMNS: TableColumn<RosterSortKey>[] = [
  { label: 'Foster', width: '32%', sortKey: 'foster' },
  { label: 'Phone', width: '19%' },
  { label: 'Status', width: '20%', sortKey: 'status' },
  { label: 'Placements', width: '16%', sortKey: 'placements' },
  { label: 'Action', width: `${ACTION_COLUMN_WIDTH}%` },
];

interface FosterRosterTableProps {
  /** The current page of homes, already ordered by the server. */
  homes: FosterHome[];
  /** Every home on the roster, not just this page — what the section header counts. */
  totalCount: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  hasError: boolean;
  onRetry: () => void;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** The page's search term, so an empty page can say it is a search that matched nothing. */
  searchQuery: string;
  sortKey: RosterSortKey | null;
  sortDir?: SortDir;
  onSort: (key: RosterSortKey) => void;
  onView: (home: FosterHome) => void;
  onAdd: () => void;
}

/**
 * The rescue's saved foster homes — the people on file, including ones fostering nothing right now.
 * Distinct from "Devices with a Foster", which lists placements. Ordered and paginated server-side;
 * this renders the page it is handed.
 */
export default function FosterRosterTable({
  homes,
  totalCount,
  currentPage,
  totalPages,
  loading,
  hasError,
  onRetry,
  pageSize,
  onPageChange,
  searchQuery,
  sortKey,
  sortDir,
  onSort,
  onView,
  onAdd,
}: FosterRosterTableProps) {
  const hasData = homes.length > 0;
  const showSkeleton = loading && !hasData && !hasError;

  return (
    <CollapsibleSection
      title="Foster Roster"
      count={totalCount}
      description="Fosters you've saved. Their household stays on file between placements, so Safe Zone and WiFi carry forward."
      headerAction={
        <Button variant="green" size="sm" onClick={onAdd}>
          <svg
            className={styles.headerBtnIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Foster
        </Button>
      }
    >
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <TableHead columns={ROSTER_COLUMNS} sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
          <tbody>
            {/*
              Only when there is nothing else to show. A refetch that fails leaves the last good
              page in hand — and every mutation on this page refetches — so blanking on
              `hasError` alone would drop the user out of a roster they were working in over one
              bad response. The hook drops rows that answer a different cut, so an empty list
              here means nothing answers the current one.
            */}
            {hasError && !hasData ? (
              <tr>
                <td colSpan={ROSTER_COLUMNS.length} className={styles.errorRow} role="alert">
                  <span className={styles.errorRowContent}>
                    <span>Could not load your foster roster.</span>
                    <Button variant="ghost" size="sm" onClick={onRetry}>
                      Retry
                    </Button>
                  </span>
                </td>
              </tr>
            ) : showSkeleton ? (
              <SkeletonRows count={3} columns={ROSTER_COLUMNS.length} />
            ) : totalCount === 0 && searchQuery.trim() ? (
              /*
                A search that matched nothing is not an empty roster: the "add your first foster"
                invitation below would tell a rescue with a full roster that it has none, and offer
                to fix it by retyping someone already on file.
              */
              <tr>
                <td colSpan={ROSTER_COLUMNS.length} className={styles.emptyState}>
                  {`No saved fosters matching "${searchQuery.trim()}"`}
                </td>
              </tr>
            ) : totalCount === 0 ? (
              <tr className={styles.emptyRow}>
                <td colSpan={ROSTER_COLUMNS.length}>
                  <div className={styles.emptyHeading}>No saved fosters yet</div>
                  <div className={styles.emptyBody}>
                    Add the fosters you already work with, or save one as you assign a device.
                  </div>
                  <Button variant="green" onClick={onAdd}>
                    Add your first foster
                  </Button>
                </td>
              </tr>
            ) : (
              homes.map((home) => (
                <tr key={home.id}>
                  <td>
                    <div className={styles.cellMain}>{home.fullName}</div>
                    {home.email && <div className={styles.cellSub}>{home.email}</div>}
                  </td>
                  <td className={styles.nowrap}>{home.phone ? formatPhoneNumber(home.phone) : '—'}</td>
                  <td>
                    {home.activePlacementCount > 0 ? (
                      `Fostering (${home.activePlacementCount})`
                    ) : (
                      <span className={styles.textMuted}>Not fostering</span>
                    )}
                  </td>
                  <td>{home.placementCount}</td>
                  <td>
                    <div className={styles.rowActions}>
                      <Button variant="ghost" size="sm" onClick={() => onView(home)}>
                        View
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
